import React, { useContext, useMemo } from 'react';

import { LocalizationContext, useLocalizedStrings } from 'app/localization/Context';
import { DeviceType, WifiDoorAndMotionSensorBatteryStatus } from 'app/model/Device';

import { ReactComponent as IconDoorOpen } from 'images/device/status-icon/door-open.svg';
import { ReactComponent as IconDoorClose } from 'images/device/status-icon/door-close.svg';
import { ReactComponent as IconMotionStandby } from 'images/device/status-icon/motion-standby.svg';
import { ReactComponent as IconMotionDetected } from 'images/device/status-icon/motion-detected.svg';
import { ReactComponent as IconSoundDetected } from 'images/device/status-icon/sound-detected.svg';
import { ReactComponent as IconSoundMute } from 'images/device/status-icon/sound-mute.svg';
import { ReactComponent as IconLightBright } from 'images/device/status-icon/light-bright.svg';
import { ReactComponent as IconLightNormal } from 'images/device/status-icon/light-normal.svg';
import { ReactComponent as IconLightOff } from 'images/device/status-icon/light-off.svg';
import { ReactComponent as IconTemperature1 } from 'images/device/status-icon/temperature-1.svg';
import { ReactComponent as IconTemperature2 } from 'images/device/status-icon/temperature-2.svg';
import { ReactComponent as IconTemperature3 } from 'images/device/status-icon/temperature-3.svg';
import { ReactComponent as IconTemperature4 } from 'images/device/status-icon/temperature-4.svg';
import { ReactComponent as IconHumidity } from 'images/device/status-icon/humidity.svg';

import { ReactComponent as IconBattery13 } from 'images/device/battery/battery-1-3.svg';
import { ReactComponent as IconBattery23 } from 'images/device/battery/battery-2-3.svg';
import { ReactComponent as IconBatteryCharging } from 'images/device/battery/battery-charging.svg';
import { ReactComponent as IconBatteryPluggedInNotCharging } from 'images/device/battery/battery-plugged-in-not-charging.svg';
import { ReactComponent as IconBatteryEmpty } from 'images/device/battery/battery-empty.svg';
import { ReactComponent as IconBatteryNotDetected } from 'images/device/battery/battery-not-detected.svg';
import { ReactComponent as IconBatteryFullDisconnected } from 'images/device/battery/battery-full-disconnected.svg';


import './All.scss'

export default function SensorControl({ device }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    const containerClass = device.active ?
        "device-detail-control with-flex sensor-active" : "device-detail-control with-flex sensor-inactive";

    const list = useMemo(() => {
        let labelTexts;
        switch (device.type) {
            case DeviceType.motion:
            case DeviceType.wifiMotionSensor:
                labelTexts = [device.state ? s.motionDetected : s.motionNotDetected];
                break;
            case DeviceType.doorSensor:
            case DeviceType.wifiDoorSensor:
                labelTexts = [device.state ? s.open : s.closed];
                break;
            case DeviceType.multipurposeSensor:
                labelTexts = [
                    device.motion ? s.motionDetected : s.motionNotDetected,
                    device.sound ? s.soundDetected : s.soundNotDetected,
                    s.lightLevels[device.light_level],
                    s.cDegreeFunc(l.localizeNumber(device.temperature)),
                    s.percentFunc(l.localizeNumber(device.humidity)),
                ];
                break;
            default:
                labelTexts = [""]
                break;
        }

        let deviceIcons;
        switch (device.type) {
            case DeviceType.motion:
            case DeviceType.wifiMotionSensor:
                deviceIcons = [device.state ? IconMotionDetected : IconMotionStandby];
                break;
            case DeviceType.doorSensor:
            case DeviceType.wifiDoorSensor:
                deviceIcons = [device.state ? IconDoorOpen : IconDoorClose];
                break;
            case DeviceType.multipurposeSensor:
                deviceIcons = [
                    device.motion ? IconMotionDetected : IconMotionStandby,
                    device.sound ? IconSoundDetected : IconSoundMute,
                    device.light_level <= 1 ? IconLightOff : (device.light_level <= 4 ? IconLightNormal : IconLightBright),
                    device.temperature <= 20 ? IconTemperature1 : (device.temperature <= 25 ? IconTemperature2 : (device.temperature <= 30 ? IconTemperature3 : IconTemperature4)),
                    IconHumidity,
                ];
                break;
            default:
                deviceIcons = [IconDoorOpen]
                break;
        }

        let all = [];
        for (let i = 0; i < labelTexts.length; i++) {
            all.push({ labelText: labelTexts[i], deviceIcon: deviceIcons[i] })
        }
        return all;
    }, [device, s, l]);

    
    return (
        <div className={containerClass}>
            {list.map(
                (item, index) => <div className="sensor-container" key={"key-sensor-container-" + String(index)}>
                    {React.createElement(item.deviceIcon, { className: "icon" })}
                    <div className="label" dir="rtl">
                        {item.labelText}
                    </div>
                </div>
            )}
        </div>
    );
}

export function WifiSensorBattery({ device }) {
    const localizeNumber = useContext(LocalizationContext).localizeNumber;
    const s = useLocalizedStrings();

    const percentage = useMemo(() => {
        if (device.battery_level !== undefined) {
            return localizeNumber(String(device.battery_level)) + '%';
        }
        return "";
    }, [device, localizeNumber]);

    const icon = useMemo(() => {
        switch (device.battery_status) {
            case WifiDoorAndMotionSensorBatteryStatus.charging:
                return IconBatteryCharging;
            case WifiDoorAndMotionSensorBatteryStatus.notCharging:
                if (device.power_enabled) {
                    return IconBatteryPluggedInNotCharging;
                } else {
                    if (device.battery_level >= 91) { // 90 - 100
                        return IconBatteryFullDisconnected;
                    } else if (device.battery_level >= 60) { // 60 - 90
                        return IconBattery23;
                    } else if (device.battery_level >= 30) { // 30 - 60
                        return IconBattery13;
                    } else { // 0 - 30
                        return IconBatteryEmpty;
                    }
                }
            case WifiDoorAndMotionSensorBatteryStatus.noBatteryDetected:
            default:
                return IconBatteryNotDetected;
        }
    }, [device]);

    return (
        <div className="device-detail-battery">
            {
                (device.battery_status === WifiDoorAndMotionSensorBatteryStatus.noBatteryDetected) ?
                    <div className="not-detected">
                        {s.noBatteryDetected}
                    </div> :
                    <div className="percentage">
                        {percentage}
                    </div>
            }
            {
                React.createElement(icon, { className: "icon" })
            }
        </div>
    );
}